import { useMemo } from "react";
import { useLocation } from "react-router-dom-v5-compat";
import { Funnel } from "@b2bportal/core-types";
import {
  type PackageUpSellAttribution,
  parsePackageUpSellAttribution,
} from "./packageUpSellParams";
import { useGetCurrentFunnel } from "@/utilities/hooks/useGetCurrentFunnel";

/**
 * The attribution to spread into any package event emitted from React:
 *
 *   properties: { ...existing, ...usePackageUpSellAttribution() }
 */
export const usePackageUpSellAttribution = (): PackageUpSellAttribution => {
  const { search } = useLocation();

  return useMemo(() => parsePackageUpSellAttribution(search), [search]);
};

export const usePackageEventAttribution = (): Omit<
  PackageUpSellAttribution,
  "flight_trip_id"
> => {
  const { entry_source, rbc_price_tab } = usePackageUpSellAttribution();

  return useMemo(
    () => ({ entry_source, ...(rbc_price_tab ? { rbc_price_tab } : {}) }),
    [entry_source, rbc_price_tab]
  );
};

/**
 * Empty outside the packages funnel: the hotel list and cover events are shared
 * with the standalone hotels funnel, where `entry_source` already carries the
 * offers attribution.
 */
export const usePackageFunnelAttribution = (): Partial<
  Omit<PackageUpSellAttribution, "flight_trip_id">
> => {
  const funnel = useGetCurrentFunnel();
  const attribution = usePackageEventAttribution();
  const isPackageFunnel = funnel === Funnel.Package;

  return useMemo(
    () => (isPackageFunnel ? attribution : {}),
    [isPackageFunnel, attribution]
  );
};
